/**
 * Loads the Google Identity Services client script once and resolves when the
 * `google.accounts.oauth2` namespace is ready. Shared by the Google OAuth
 * composables so the script tag is never injected more than once.
 *
 * The rendered "Sign in with Google" button is localized by the script's `hl`
 * query param, NOT by the `locale` option passed to renderButton (which GIS
 * ignores in practice). Pass the current locale so the button matches the UI
 * language. Because the script loads only once, the first caller's locale wins
 * for the page — callers pass the same locale, so this is deterministic.
 *
 * @param {string} [locale] - BCP-47 language code for the button UI (e.g. "hu").
 * @returns {Promise<void>}
 */
export function loadGisScript(locale) {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector(
      "script[src^=\"https://accounts.google.com/gsi/client\"]"
    );
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Google Identity Services")),
        { once: true }
      );
      return;
    }
    const script = document.createElement("script");
    script.src = locale
      ? `https://accounts.google.com/gsi/client?hl=${encodeURIComponent(locale)}`
      : "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(script);
  });
}
